import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {HouseholdWithStores} from '../../models/household';
import {AppEventsService} from '../../services/app-events/app-events.service';
import {ConfirmService} from '../../services/confirm/confirm.service';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {HouseholdService} from '../../services/property/household.service';
import {UiToastService} from '../../shared/ui';
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: 'app-manage-household',
  templateUrl: './manage-household.component.html',
  styleUrls: ['./manage-household.component.scss'],
  standalone: false,
})
export class ManageHouseholdComponent implements OnInit, OnDestroy {
  private sub: any;

  private readonly log: AppLogger;

  householdId: number;
  household: HouseholdWithStores;

  accessDenied = false;
  deletingHousehold = false;

  constructor(
    private householdService: HouseholdService,
    private route: ActivatedRoute,
    public toastService: UiToastService,
    private appLogger: AppLoggerService,
    private appEvents: AppEventsService,
    private confirm: ConfirmService,
    private router: Router,
    private pageHeader: PageHeaderService,
  ) {
    this.log = this.appLogger.createForClass(ManageHouseholdComponent);
  }

  ngOnInit(): void {
    this.sub = this.route.params.subscribe((params) => {
      this.householdId = +params.id;
      this.loadHousehold(this.householdId);
    });
    this.pageHeader.setBackButton({
      label: 'Back',
      preferHistoryBack: true
    })
  }

  ngOnDestroy(): void {
    this.pageHeader.clearAll();
    this.sub?.unsubscribe?.();
  }

  private loadHousehold(householdId: number): void {
    this.householdService.householdById(householdId).subscribe({
      next: (data) => {
        this.household = data;
        this.pageHeader.setPageTitle(this.household?.name ?? 'Household');
      },
      error: (error) => {
        if (error?.status === 403) {
          this.accessDenied = true;
        }
      },
    });
  }

  openDeleteHouseholdModal(): void {
    if (this.household?.is_default) {
      this.toastService.error('You cannot delete your default household. Switch the default household first.', {durationMs: 4000});
      return;
    }

    if (!this.household?.name) {
      this.toastService.error('Household not loaded yet.', {durationMs: 3000});
      return;
    }

    this.confirm
      .ask({
        title: 'Delete household',
        message: `Delete household "${this.household.name}"? This cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmBtnClass: 'btn-danger',
        modalOptions: {
          size: 'md',
          backdrop: 'static',
        },
      })
      .then((ok) => {
        if (!ok) return;
        this.deleteHousehold();
      });
  }

  private deleteHousehold(): void {
    if (this.deletingHousehold) return;

    this.deletingHousehold = true;

    this.householdService.delete(this.householdId).subscribe({
      next: () => {
        this.toastService.success('Household deleted', {durationMs: 3000});
        this.appEvents.notifyHouseholdChanged();
        this.deletingHousehold = false;
        this.router.navigate(['/profile']);
      },
      error: (e) => {
        this.log.error('Failed to delete household', e, { householdId: this.householdId });
        this.toastService.error('Failed to delete household', {durationMs: 6000});
        this.deletingHousehold = false;
      },
    });
  }
}
